import React, { useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList
} from 'recharts';
import { FaTrash, FaSpinner } from 'react-icons/fa';
import ChartEditor from './ChartEditor';
import './ChartDisplay.css';

const PRIMARY_COLOR = '#233e80';
const SECONDARY_PRIMARY_COLOR = '#1a6ed8';
const COLORS = [PRIMARY_COLOR, SECONDARY_PRIMARY_COLOR, '#3b9efc', '#1f5bd8', '#2563eb', '#0f2d73'];

export default function ChartDisplay({ chartData, chartId, gridPosition, onDelete, onUpdate }) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  if (!chartData) {
    return (
      <div className="chart-display-empty">
        <div className="chart-display-placeholder">
          <span className="chart-placeholder-icon">📊</span>
          <p>Click the + button to create a new chart</p>
        </div>
      </div>
    );
  }

  const renderChart = () => {
    const { type, data, config = {} } = chartData;
    const { 
      xKey = 'name', 
      yKey = 'value', 
      title = 'Chart',
      xAxisLabel,
      yAxisLabel,
      showDataPoints = true,
      showGrid = true,
      showLegend = true,
      color = PRIMARY_COLOR,
      chartHeight: configHeight,
      legendLabel
    } = config;

    // Calculate chart height based on data points or use config
    // Make height flexible to fit all data
    const dataPoints = data ? data.length : 0;
    let chartHeight;
    
    if (type === 'pie') {
      // For pie charts, use a more compact size
      // Reduce base size and use space more efficiently
      const baseSize = Math.max(400, 350 + (dataPoints * 25));
      chartHeight = baseSize;
    } else {
      // For bar/line/area charts, calculate based on data points
      // Allow more space for each data point to prevent crowding
      const baseHeight = Math.max(300, 250 + (dataPoints * 40));
      chartHeight = configHeight || baseHeight;
    }
    
    const gradientId = `colorGradient-${chartId || Date.now()}`;
    const legendName = legendLabel || yAxisLabel || yKey || 'Value';

    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
              <XAxis 
                dataKey={xKey} 
                stroke="#64748b"
                tick={{ fontSize: 11, fill: '#64748b' }}
                label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: { fontSize: 11 } } : undefined}
              />
              <YAxis 
                stroke="#64748b"
                tick={{ fontSize: 11, fill: '#64748b' }}
                label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } } : undefined}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} 
              />
              {showLegend && <Legend wrapperStyle={{ fontSize: '11px' }} />}
              <Bar dataKey={yKey} fill={color} radius={[8, 8, 0, 0]} name={legendName} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
              <XAxis 
                dataKey={xKey} 
                stroke="#64748b"
                tick={{ fontSize: 11, fill: '#64748b' }}
                label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: { fontSize: 11 } } : undefined}
              />
              <YAxis 
                stroke="#64748b"
                tick={{ fontSize: 11, fill: '#64748b' }}
                label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } } : undefined}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} 
              />
              {showLegend && <Legend wrapperStyle={{ fontSize: '11px' }} />}
              <Line 
                type="monotone" 
                dataKey={yKey} 
                stroke={color} 
                strokeWidth={3}
                dot={showDataPoints ? { fill: color, r: 5 } : false}
                activeDot={{ r: 7 }}
                name={legendName}
              >
                {showDataPoints && (
                  <LabelList
                    dataKey={yKey}
                    position="top"
                    fill={color}
                    fontSize={10}
                    formatter={(value) => value}
                  />
                )}
              </Line>
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        // Calculate responsive outerRadius based on chart height
        // Minimize margins to reduce white space while keeping room for labels
        // Account for margins (top: 10px, bottom: 10px) and legend space (~60px)
        const availableHeight = chartHeight - 20 - 60; // Top/bottom margins + legend space
        // Use larger percentage of available space for the pie itself
        const outerRadius = Math.min(Math.max(availableHeight * 0.45, 80), 140);
        // Minimize margins - only keep side margins for labels, reduce top/bottom
        const pieMargins = { top: 10, right: 60, bottom: 10, left: 60 };
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <PieChart margin={pieMargins}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => {
                  // Show label only if slice is large enough to be readable
                  if (percent < 0.03) return ''; // Hide labels for very small slices
                  // Truncate very long names to prevent overflow
                  const displayName = name.length > 25 ? name.substring(0, 22) + '...' : name;
                  return `${displayName}: ${(percent * 100).toFixed(0)}%`;
                }}
                outerRadius={outerRadius}
                fill={PRIMARY_COLOR}
                dataKey={yKey}
                style={{ fontSize: '10px' }}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} 
              />
              {showLegend && (
                <Legend 
                  align="left"
                  wrapperStyle={{ paddingTop: '10px', fontSize: '10px' }} 
                />
              )}
            </PieChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
              <XAxis 
                dataKey={xKey} 
                stroke="#64748b"
                tick={{ fontSize: 11, fill: '#64748b' }}
                label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: { fontSize: 11 } } : undefined}
              />
              <YAxis 
                stroke="#64748b"
                tick={{ fontSize: 11, fill: '#64748b' }}
                label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } } : undefined}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} 
              />
              {showLegend && <Legend wrapperStyle={{ fontSize: '11px' }} />}
              <Area 
                type="monotone" 
                dataKey={yKey} 
                stroke={color} 
                fill={`url(#${gradientId})`}
                strokeWidth={2}
                name={legendName}
              />
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={color} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <div className="chart-display-error">
            <p>Unsupported chart type: {type}</p>
          </div>
        );
    }
  };

  const handleDeleteClick = () => {
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    if (chartId && onDelete) {
      onDelete(chartId);
    }
    setShowConfirmDelete(false);
  };

  const handleCancelDelete = () => {
    setShowConfirmDelete(false);
  };

  const handleChartClick = () => {
    setShowEditor(true);
  };

  const handleEditorSave = async (updatedChartData, id) => {
    if (!id || (typeof id === 'string' && id.startsWith('temp-'))) {
      alert('Please wait until the chart finishes saving before editing.');
      return;
    }

    setIsUpdating(true);
    try {
      if (onUpdate) {
        const result = await onUpdate(id, updatedChartData);
        if (result === false) {
          setIsUpdating(false);
          return;
        }
      }
      setShowEditor(false);
    } catch (error) {
      console.error('Error saving chart:', error);
      setIsUpdating(false);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="chart-display-container">
      <div className="chart-display-header">
        <h3 className="chart-display-title">{chartData.config?.title || 'Chart'}</h3>
        {chartId ? (
          <button
            className="chart-delete-button"
            onClick={handleDeleteClick}
            title="Delete chart"
          >
            <FaTrash />
          </button>
        ) : null}
      </div>
      <div className="chart-display-body" onClick={handleChartClick} style={{ cursor: 'pointer', position: 'relative' }}>
        {renderChart()}
        
        {/* Loading Overlay on Chart */}
        {isUpdating && (
          <div className="chart-display-updating-overlay">
            <div className="chart-display-updating-content">
              <FaSpinner className="chart-display-updating-spinner" />
              <p>Updating Chart...</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Chart Editor */}
      {showEditor && (
        <ChartEditor
          chartData={chartData}
          chartId={chartId}
          isOpen={showEditor}
          onClose={() => !isUpdating && setShowEditor(false)}
          onSave={handleEditorSave}
          isSaving={isUpdating}
        />
      )}

      {/* Confirmation Dialog */}
      {showConfirmDelete && (
        <div className="chart-delete-confirm-overlay" onClick={handleCancelDelete}>
          <div className="chart-delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h4>Delete Chart?</h4>
            <p>Are you sure you want to delete this chart? This action cannot be undone.</p>
            <div className="chart-delete-confirm-buttons">
              <button
                className="chart-delete-confirm-button chart-delete-cancel"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button
                className="chart-delete-confirm-button chart-delete-confirm"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

